
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { ImportResult } from "@/components/tools/elf-name-generator/types";

interface BulkImportProps {
  onImport: (items: {name: string, category?: string}[]) => Promise<ImportResult>;
  nameField: string;
  placeholder: string;
}

const BulkImport: React.FC<BulkImportProps> = ({
  onImport,
  nameField,
  placeholder
}) => {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  
  const handleImport = async () => {
    if (!text.trim()) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      // Parse input to extract names
      const names = text
        .split('\n')
        .flatMap(line => line.split(','))
        .map(item => item.trim())
        .filter(item => item.length > 0);
      
      // Convert to the required format
      const items = names.map(name => ({
        name,
        category: category || undefined
      }));
      
      const importResult = await onImport(items);
      setResult(importResult);
      
      // Clear the input on successful import
      if (importResult.success > 0) {
        setText("");
      }
    } catch (error) {
      console.error("Import error:", error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="p-4">
      <div className="space-y-4">
        <div>
          <p className="mb-2 text-sm font-medium">
            Enter one {nameField} per line or separate with commas
          </p>
          <Textarea
            placeholder={placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            className="font-mono"
          />
        </div>
        
        <div>
          <p className="mb-2 text-sm font-medium">Category (optional)</p>
          <Input
            placeholder="Enter category (e.g., custom, festive, winter)"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            This category will be applied to all imported items
          </p>
        </div>
        
        <Button 
          onClick={handleImport} 
          disabled={loading || !text.trim()}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Importing...
            </>
          ) : (
            `Import ${nameField}s`
          )}
        </Button>
        
        {result && (
          <div className="mt-4 p-3 rounded border">
            <p>
              Import complete: 
              <span className="text-green-600 font-semibold ml-1">{result.success} successful</span>
              {result.failed > 0 && (
                <span className="text-red-600 font-semibold ml-1">{result.failed} failed</span>
              )}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default BulkImport;
